import { useTranslation } from 'react-i18next';
import { CreditCard } from '@styled-icons/material/CreditCard';
import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalHeaderIconBox,
} from '@increasecard/typed-components';
import { useState } from 'react';
import { useUpdateInvoice } from '../../features/invoice/invoiceQueries';
import { PathSwitcher } from '../common/PathSwitcher';
import { CustomerPaymentMethods } from './CustomerPaymentMethods';
import { PaymentMethodCreator } from './PaymentMethodCreator';
import { Invoice, PaymentMethod, PaymentReference } from '../../types';
import { ApiErrorMessage } from '../ErrorMessage';

export interface AddPMToInvoiceModalProps {
  visible: boolean;
  onClose: VoidFunction;
  onExited?: VoidFunction;
  onSucces?: (paymentMethod: PaymentMethod) => void;
  invoice: Invoice | null;
}
// For now, this modal can only operate over an invoice.
export function AddPMToInvoiceModal({
  visible,
  invoice,
  onClose,
}: AddPMToInvoiceModalProps): JSX.Element | null {
  const { t } = useTranslation();
  const [createPM, setCreatePM] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  if (!invoice) return null;

  const isPMChange = !!invoice.latest_payment_method;
  const label = isPMChange
    ? t('common.change_payment_method')
    : t('common.add_payment_method');

  const paymentReference: PaymentReference = {
    reference_type: 'invoice',
    reference_id: invoice.id,
  };

  const handleClose = () => {
    setCreatePM(false);
    setIsSuccess(false);
    onClose();
  };

  const getContent = () => {
    if (isSuccess) {
      return (
        <>
          El método de pago se cambió con éxito
          <ModalFooter align="center">
            <Button onClick={handleClose} buttonType="primary">
              {t('common.close')}
            </Button>
          </ModalFooter>
        </>
      );
    }
    return (
      <>
        Elija o cree un metodo de pago para cobrar la factura
        <PathSwitcher
          optionATitle="Elegir un medio de pago existente"
          optionBTitle="Crear nuevo medio de pago"
          value={createPM}
          onChange={setCreatePM}
          style={{ margin: '16px 0' }}
        />
        {createPM ? (
          <PaymentMethodCreator
            onSuccess={() => setIsSuccess(true)}
            customerId={invoice.customer.id}
            pagoticId={invoice.pagotic_id}
            groupedCollectMethods={invoice.collect_methods}
            paymentReference={paymentReference}
          />
        ) : (
          <UpdatePaymentMethod
            invoice={invoice}
            onClose={handleClose}
            buttonLabel={label}
            onSuccess={() => setIsSuccess(true)}
          />
        )}
      </>
    );
  };

  return (
    <Modal
      align="center"
      onClose={handleClose}
      visible={visible}
      width="800"
      onOk={() => null}
      onCancel={() => null}
    >
      <ModalHeaderIconBox
        colorPalette="pay"
        icon={<CreditCard size="48px" />}
      />
      <ModalHeader align="center">{label}</ModalHeader>
      <ModalContent>{getContent()}</ModalContent>
    </Modal>
  );
}

interface UpdatePaymentMethodProps {
  invoice: Invoice;
  onClose: VoidFunction;
  buttonLabel: string;
  onSuccess: VoidFunction;
}

function UpdatePaymentMethod({
  invoice,
  onClose,
  buttonLabel,
  onSuccess,
}: UpdatePaymentMethodProps) {
  const { t } = useTranslation();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>();
  const { mutate, isSuccess, isError, isLoading, error } = useUpdateInvoice();

  const handlePMSelection = (pm: PaymentMethod) => {
    setSelectedPaymentMethod(pm.id);
  };

  const handleExistingPM = () => {
    if (selectedPaymentMethod) {
      mutate(
        {
          id: invoice.id,
          latest_payment_method_id: selectedPaymentMethod,
        },
        {
          onSuccess,
        }
      );
    }
  };
  const getContent = () => {
    if (isSuccess) {
      return 'El método de pago de la factura se actualizó con éxito.';
    } else {
      return (
        <CustomerPaymentMethods
          customerId={invoice.customer.id}
          onChange={handlePMSelection}
          selectedPaymentMethodId={
            selectedPaymentMethod || invoice?.latest_payment_method?.id
          }
          groupedCollectMethods={invoice.collect_methods}
        />
      );
    }
  };

  return (
    <>
      {getContent()}
      <ApiErrorMessage error={error} />
      <ModalFooter align="center">
        {isError || isSuccess ? (
          <Button onClick={onClose} buttonType="primary">
            {t('common.close')}
          </Button>
        ) : (
          <>
            <Button
              data-testid="cancel-button"
              onClick={onClose}
              buttonType="invisible"
              disabled={isLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleExistingPM}
              disabled={!selectedPaymentMethod}
              isLoading={isLoading}
            >
              {buttonLabel}
            </Button>
          </>
        )}
      </ModalFooter>
    </>
  );
}
